'use client'

import { Dialog as ChakraDialog, Portal, useSlotRecipe } from '@chakra-ui/react'
import {
  createContext,
  forwardRef,
  type ComponentProps,
  useContext,
} from 'react'
import { assiduousDialogRecipe } from '@/design-system/recipes/dialog'
import { CloseButton, type CloseButtonProps } from '@/components/ui/CloseButton'

type ChakraDialogContentProps = ComponentProps<typeof ChakraDialog.Content>
type ChakraDialogPositionerProps = ComponentProps<
  typeof ChakraDialog.Positioner
>
type ChakraDialogBackdropProps = ComponentProps<typeof ChakraDialog.Backdrop>
type ChakraDialogHeaderProps = ComponentProps<typeof ChakraDialog.Header>
type ChakraDialogBodyProps = ComponentProps<typeof ChakraDialog.Body>
type ChakraDialogFooterProps = ComponentProps<typeof ChakraDialog.Footer>
type ChakraDialogTitleProps = ComponentProps<typeof ChakraDialog.Title>
type ChakraDialogDescriptionProps = ComponentProps<
  typeof ChakraDialog.Description
>
type DialogLayout = 'small' | 'medium' | 'large' | 'fullscreen'
const DialogLayoutContext = createContext<DialogLayout>('large')

export interface DialogCloseButtonProps
  extends Omit<CloseButtonProps, 'appearance' | 'label' | 'onClose'> {
  /** Accessible label announced for the dialog close control. */
  'aria-label'?: string
}

export interface DialogContentProps extends ChakraDialogContentProps {
  /** Props forwarded to the standard close button. */
  closeButtonProps?: DialogCloseButtonProps
  /** Hides the standard close button for non-dismissible dialogs. */
  hideCloseButton?: boolean
  /** Controls whether the dialog uses a small, large, or fullscreen shell. */
  layout?: DialogLayout
}

/** The standard close control shared by Assiduous dialogs. */
export const DialogCloseButton = forwardRef<
  HTMLButtonElement,
  DialogCloseButtonProps
>(function DialogCloseButton(
  { 'aria-label': ariaLabel = 'Close dialog', ...props },
  ref
) {
  return (
    <ChakraDialog.CloseTrigger asChild>
      <CloseButton
        ref={ref}
        label={ariaLabel}
        appearance="surface"
        {...props}
      />
    </ChakraDialog.CloseTrigger>
  )
})

const DialogBackdrop = forwardRef<HTMLDivElement, ChakraDialogBackdropProps>(
  function DialogBackdrop({ css, ...props }, ref) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const styles = recipe()
    return (
      <ChakraDialog.Backdrop
        ref={ref}
        css={[styles.backdrop, css]}
        {...props}
      />
    )
  }
)

const DialogPositioner = forwardRef<
  HTMLDivElement,
  ChakraDialogPositionerProps
>(function DialogPositioner({ css, ...props }, ref) {
  const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
  const styles = recipe()
  return (
    <ChakraDialog.Positioner
      ref={ref}
      css={[styles.positioner, css]}
      {...props}
    />
  )
})

/** The responsive Assiduous dialog shell and standard close control. */
const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent(
    {
      children,
      closeButtonProps,
      css,
      hideCloseButton = false,
      layout = 'large',
      ...props
    },
    ref
  ) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const styles = recipe({ layout })
    const layoutGeometry =
      layout === 'fullscreen'
        ? {
            h: '90dvh',
            maxH: '90dvh',
            maxW: '90dvw',
            w: '90dvw',
          }
        : layout === 'large'
          ? {
              h: 'auto',
              maxH: {
                base: 'calc(100dvh - 48px)',
                md: 'calc(100dvh - 96px)',
                lg: 'calc(100dvh - 120px)',
                xl: 'calc(100dvh - clamp(144px, 11.11vw, 160px))',
              },
              maxW: {
                base: 'calc(100% - 32px)',
                md: '600px',
                lg: '640px',
                xl: '960px',
              },
              overflow: 'hidden',
              w: {
                base: 'calc(100% - 32px)',
                md: '600px',
                lg: '640px',
                xl: 'clamp(720px, 55.56vw, 960px)',
              },
            }
          : {}

    return (
      <ChakraDialog.Content
        ref={ref}
        css={[styles.content, css]}
        {...layoutGeometry}
        {...props}
      >
        <DialogLayoutContext.Provider value={layout}>
          {children}
          {hideCloseButton ? null : <DialogCloseButton {...closeButtonProps} />}
        </DialogLayoutContext.Provider>
      </ChakraDialog.Content>
    )
  }
)

const DialogHeader = forwardRef<HTMLDivElement, ChakraDialogHeaderProps>(
  function DialogHeader({ css, ...props }, ref) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const layout = useContext(DialogLayoutContext)
    const styles = recipe({ layout })
    return (
      <ChakraDialog.Header ref={ref} css={[styles.header, css]} {...props} />
    )
  }
)

const DialogBody = forwardRef<HTMLDivElement, ChakraDialogBodyProps>(
  function DialogBody({ css, ...props }, ref) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const layout = useContext(DialogLayoutContext)
    const styles = recipe({ layout })
    return <ChakraDialog.Body ref={ref} css={[styles.body, css]} {...props} />
  }
)

const DialogFooter = forwardRef<HTMLDivElement, ChakraDialogFooterProps>(
  function DialogFooter({ css, ...props }, ref) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const layout = useContext(DialogLayoutContext)
    const styles = recipe({ layout })
    return (
      <ChakraDialog.Footer ref={ref} css={[styles.footer, css]} {...props} />
    )
  }
)

const DialogTitle = forwardRef<HTMLDivElement, ChakraDialogTitleProps>(
  function DialogTitle({ css, ...props }, ref) {
    const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
    const styles = recipe()
    return <ChakraDialog.Title ref={ref} css={[styles.title, css]} {...props} />
  }
)

const DialogDescription = forwardRef<
  HTMLDivElement,
  ChakraDialogDescriptionProps
>(function DialogDescription({ css, ...props }, ref) {
  const recipe = useSlotRecipe({ recipe: assiduousDialogRecipe })
  const styles = recipe()
  return (
    <ChakraDialog.Description
      ref={ref}
      css={[styles.description, css]}
      {...props}
    />
  )
})

export const Dialog = {
  ActionTrigger: ChakraDialog.ActionTrigger,
  Backdrop: DialogBackdrop,
  Body: DialogBody,
  CloseButton: DialogCloseButton,
  CloseTrigger: ChakraDialog.CloseTrigger,
  Content: DialogContent,
  Description: DialogDescription,
  Footer: DialogFooter,
  Header: DialogHeader,
  Portal,
  Positioner: DialogPositioner,
  Root: ChakraDialog.Root,
  Title: DialogTitle,
  Trigger: ChakraDialog.Trigger,
}
